import React, { useMemo } from 'react';
import { RCPWithQueryParams } from 'util/routing';
import { Filter, Model } from 'util/backendapi/models/api.interfaces';
import {
  observationPointTimeDependentFieldListFilters,
  observationPointTimeDependentFieldListColumns,
  ObservationPointTimeDependentFieldListView,
} from './ObservationPointTimeDependentFieldListView';
import { useReportState } from 'hooks/use-report-state';
import { instrumentReliabilityListDuck } from 'ducks/instrument-reliability/list';
import { ReportViewProps } from 'components/modules/report/report-types';
import { t, Trans } from '@lingui/macro';
import { useGetApi } from 'hooks/use-get-api';
import { errorToString } from 'util/backendapi/error';

type OwnProps =
  RCPWithQueryParams<Filter.ObservationPointTimeDependentFieldFilters>;

export function InstrumentReliabilityListView(props: {
  reportProps: ReportViewProps<Model.ReportsObservationPointTimeDependentField>;
  valueOptions: Record<string, string>;
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: string;
}) {
  const { reportProps, valueOptions, isLoading, isError, errorMessage } = props;

  return (
    <ObservationPointTimeDependentFieldListView
      reportProps={reportProps}
      header={<Trans>Instrument reliability report</Trans>}
      reportUrl="/reports/instrument-reliability/"
      fileNameBase={t`Instrument reliability`}
      valueOptions={valueOptions}
      isLoading={isLoading ?? false}
      isError={isError ?? false}
      errorMessage={errorMessage}
    />
  );
}

export const InstrumentReliabilityListScreen = (ownProps: OwnProps) => {
  const columns = useMemo(() => {
    return observationPointTimeDependentFieldListColumns();
  }, []);

  const props = {
    reportProps: useReportState(
      ownProps,
      columns,
      observationPointTimeDependentFieldListFilters,
      instrumentReliabilityListDuck,
      (state) => state.instrumentReliability.list
    ),
  };

  const [reliabilities] = useGetApi('/observation-point-reliabilities/');

  const valueOptions = useMemo(() => {
    const options: Record<string, string> = {};
    if (reliabilities.data) {
      reliabilities.data.forEach(({ code, name }) => {
        options[code] = `${code} - ${name}`;
      });
    }
    return options;
  }, [reliabilities]);

  return (
    <InstrumentReliabilityListView
      {...props}
      valueOptions={valueOptions}
      isLoading={reliabilities.isLoading}
      isError={reliabilities.isError}
      errorMessage={
        reliabilities.error ? errorToString(reliabilities.error) : undefined
      }
    />
  );
};
